/**
 * `none` is the fail-closed fallback: an account whose `role` is missing or unknown can
 * sign in but may do nothing — the layout parks it on /no-access (PLAN §3 #3).
 */
export type Role = "admin" | "specialist" | "manager" | "none";

export const STAFF_ROLES = ["admin", "specialist", "manager"] as const satisfies readonly Role[];

export type Action =
  | "patient:create"
  | "patient:read"
  | "patient:update"
  | "patient:archive"
  | "episode:write"
  | "visit:create"
  | "visit:update"
  | "task:manage"
  | "file:manage"
  | "appointment:manage"
  | "appointment:move"
  | "settings:manage"
  // Sprint 9: the read partition + money capabilities.
  | "clinical:read"
  | "pricing:manage"
  | "invoice:manage"
  | "gift:manage"
  // Notification settings (docs/PLAN-notification-settings.md): admin + manager, its own
  // action so `settings:manage` (accounts, later) stays admin-only.
  | "notifications:manage";

const MATRIX: Record<Role, Action[]> = {
  admin: [
    "patient:create",
    "patient:read",
    "patient:update",
    "patient:archive",
    "episode:write",
    "visit:create",
    "visit:update",
    "task:manage",
    "file:manage",
    "appointment:manage",
    "appointment:move",
    "settings:manage",
    "clinical:read",
    "pricing:manage",
    "invoice:manage",
    "gift:manage",
    "notifications:manage",
  ],
  // Specialists own the full patient lifecycle (incl. archive — PO decision, Sprint 6);
  // clinic-wide settings and money management stay admin/manager-side (OQ-18).
  specialist: [
    "patient:create",
    "patient:read",
    "patient:update",
    "patient:archive",
    "episode:write",
    "visit:create",
    "visit:update",
    "task:manage",
    "file:manage",
    "appointment:manage",
    "appointment:move",
    "clinical:read",
  ],
  // Managers run schedule + money and register patients, but never see clinical
  // content: no `clinical:read`, no episode/visit/task/file capabilities (OQ-17).
  manager: [
    "patient:create",
    "patient:read",
    "patient:update",
    "appointment:manage",
    "appointment:move",
    "pricing:manage",
    "invoice:manage",
    "gift:manage",
    "notifications:manage",
  ],
  none: [],
};

/** Authorization check — enforced in use-cases, not only the UI. */
export function can(role: Role, action: Action): boolean {
  return MATRIX[role]?.includes(action) ?? false;
}

/**
 * Session `role` strings are free-form; only the three staff roles map to capabilities.
 * Anything else — missing, empty, a typo — is `none` and can open nothing (fail closed).
 */
export function toRole(role: string | null | undefined): Role {
  return (STAFF_ROLES as readonly string[]).includes(role ?? "") ? (role as Role) : "none";
}

export function isStaffRole(role: Role): role is (typeof STAFF_ROLES)[number] {
  return role !== "none";
}
